import React, { useEffect, useState } from 'react';
import { supabase } from '../../../utils/supabaseClient';
import { format } from 'date-fns';
import Modal from '../../../components/Modal';

// Utility to strip ALL HTML/script tags and angle brackets
function stripHtmlTags(str) {
  if (!str) return '';
  // Remove all HTML tags and any angle brackets
  return str.replace(/<\/?[^>]+(>|$)/g, '').replace(/[<>]/g, '');
}

function Account() {
  const [name, setName] = useState('');
  const [originalName, setOriginalName] = useState('');
  const [email, setEmail] = useState('');
  const [confirmedAt, setConfirmedAt] = useState('');
  const [planName, setPlanName] = useState('Free');
  const [showUpdate, setShowUpdate] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [isFadingOut, setIsFadingOut] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session_id');
    if (sessionId) {
      fetch('/api/confirm-session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionId }),
      })
        .then(() => window.location.replace('/account'))
        .catch(err => console.error('Confirm session error:', err));
    }
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      setErrorMsg('');
      setSuccessMsg('');

      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) {
        setErrorMsg(userError?.message || 'Not signed in');
        return;
      }

      setEmail(user.email);
      if (user.confirmed_at) {
        setConfirmedAt(format(new Date(user.confirmed_at), 'PPP p'));
      }

      const { data: profile, error: profileError } = await supabase
        .from('accounts')
        .select('name, plan')
        .eq('id', user.id)
        .maybeSingle();

      if (profileError || !profile) {
        console.error('Error fetching profile:', profileError);
        setErrorMsg('Failed to load profile');
        return;
      }

      const plan = (profile.plan || 'free').toLowerCase();
      setPlanName(plan === 'pro' ? 'Pro' : 'Free');
      setName(profile.name || '');
      setOriginalName(profile.name || '');
    };

    fetchData();
  }, []);

  useEffect(() => {
    setShowUpdate(name !== originalName);
  }, [name, originalName]);

  const handleUpdate = async (e) => {
    e.preventDefault();
    setSuccessMsg('');
    setErrorMsg('');

    // Sanitize name again just in case
    const cleanName = stripHtmlTags(name);

    const { data: { user }, error: getUserErr } = await supabase.auth.getUser();
    if (getUserErr || !user) {
      setErrorMsg(getUserErr?.message || 'Not signed in');
      return;
    }

    const { error: updateError } = await supabase
      .from('accounts')
      .update({ name: cleanName })
      .eq('id', user.id);

    if (updateError) {
      setErrorMsg(updateError.message);
    } else {
      setOriginalName(cleanName);
      setShowUpdate(false);
      setSuccessMsg('Name updated successfully!');
    }
  };

  useEffect(() => {
    if (successMsg) {
      setIsFadingOut(false);
      const fadeTimer = setTimeout(() => setIsFadingOut(true), 2500);
      const clearTimer = setTimeout(() => setSuccessMsg(''), 3000);

      return () => {
        clearTimeout(fadeTimer);
        clearTimeout(clearTimer);
      };
    }
  }, [successMsg]);

  const handleDeleteAccount = async () => {
    try {
      const { data: { user }, error } = await supabase.auth.getUser();
      if (error || !user) throw new Error(error?.message || 'Not signed in');

      const res = await fetch('/api/delete-account', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: user.id,
          email: user.email,
          plan: planName,
        }),
      });

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || 'Failed to delete account');
      }

      console.log('✅ Account deleted successfully');
      await supabase.auth.signOut();
      window.location.href = '/account-deleted';
    } catch (err) {
      console.error('🔥 Delete error:', err);
      setErrorMsg(err.message);
    }
  };

  return (
    <>
      <header>
        <div className="section-title">
          <h1>My Account</h1>
        </div>
      </header>

      <section className="scroll">
        <div className="contain">
          <form onSubmit={handleUpdate}>
            <label htmlFor="name">Your Name:</label>
            <input
              id="name"
              type="text"
              value={name}
              onChange={(e) => setName(stripHtmlTags(e.target.value))}
              autoComplete="off"
            />

            <label htmlFor="email">Your Email:</label>
            <input id="email" type="text" value={email} disabled />

            <label htmlFor="plan">Current Plan:</label>
            <input id="plan" type="text" value={planName} disabled />

            <div className="row">
              <div>
                <label htmlFor="confirmed">Account Confirmed On:</label>
                <input id="confirmed" type="text" value={confirmedAt} disabled />
              </div>
            </div>

            {successMsg && (
              <p className={`success-message ${isFadingOut ? 'fade-out' : ''}`}>
                {successMsg}
              </p>
            )}

            {errorMsg && <p className="error-message">{errorMsg}</p>}

            {showUpdate && (
              <div className="text-center">
                <button type="submit" className="btn-outlined">
                  Update
                </button>
              </div>
            )}
          </form>

          <button
            type="button"
            className="btn-outlined fixed-bottom-right btn-delete"
            onClick={() => setShowDeleteConfirm(true)}
          >
            Delete Account
          </button>
        </div>
      </section>

      {showDeleteConfirm && (
        <Modal
          isOpen={showDeleteConfirm}
          onClose={() => setShowDeleteConfirm(false)}
          title="Are you sure you want to delete your account?"
          actions={[
            {
              label: 'Confirm Delete',
              onClick: handleDeleteAccount,
              className: 'btn-outlined btn-delete',
            },
            {
              label: 'Nevermind',
              onClick: () => setShowDeleteConfirm(false),
              className: 'btn-outlined',
            },
          ]}
        >
          <p>
            {planName === 'Pro'
              ? 'This will cancel your subscription and delete all your data indefinitely.'
              : 'This will delete all of your user data indefinitely.'}
          </p>
        </Modal>
      )}
    </>
  );
}

export default Account;
